<template>
  <div class="content">
    <PageIntro
      title="Selected work from 2018"
      subtitle="present"
      :tags="['Web', 'Brand', 'Mobile', 'Product', 'Graphic']"
    />

    <ProjectGrid />
    <CustomCursor ref="customCursorRef" />
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch, nextTick, onActivated } from "vue";
import { useRoute } from "vue-router";
import ProjectGrid from "@/components/projects/ProjectGrid.vue";
import CustomCursor from "@/components/CustomCursor.vue";
import PageIntro from "@/components/PageIntro.vue";

let masonryInstance = null;
let initTimeout = null;
let resizeTimeout = null;

const route = useRoute();
const customCursorRef = ref(null);

// Handle window resize
const handleResize = () => {
  if (resizeTimeout) {
    clearTimeout(resizeTimeout);
  }

  resizeTimeout = setTimeout(() => {
    if (masonryInstance && typeof masonryInstance.layout === "function") {
      try {
        masonryInstance.layout();
      } catch (error) {
        console.warn("Error calling masonry layout on resize:", error);
      }
    }
  }, 250);
};

const setupMasonry = () => {
  // Clear any existing timeout
  if (initTimeout) {
    clearTimeout(initTimeout);
    initTimeout = null;
  }

  loadMasonryScript()
    .then(() => {
      animateProjects();

      // Use multiple nextTick calls to ensure DOM is fully ready
      nextTick(() => {
        nextTick(() => {
          // Increased timeout to account for transitions
          initTimeout = setTimeout(() => {
            initMasonry();
          }, 500);
        });
      });
    })
    .catch((error) => {
      console.error("Failed to load Masonry script:", error);
      // Still try to animate projects even if Masonry fails to load
      animateProjects();
    });
};

const resetMasonryLayout = () => {
  // Clear any existing timeout
  if (initTimeout) {
    clearTimeout(initTimeout);
    initTimeout = null;
  }

  // Wait for transitions to complete, then reinitialize
  initTimeout = setTimeout(() => {
    initMasonry();
  }, 600);
};

onMounted(() => {
  setupMasonry();
  window.addEventListener("resize", handleResize);
});

// Handle route changes - only reinitialize if we're on the home route
watch(
  () => route.fullPath,
  (newPath) => {
    if (newPath === "/" || newPath === "/home") {
      resetMasonryLayout();
    }
  }
);

// Handle keep-alive scenarios
onActivated(() => {
  resetMasonryLayout();
});

onUnmounted(() => {
  window.removeEventListener("resize", handleResize);

  if (initTimeout) {
    clearTimeout(initTimeout);
    initTimeout = null;
  }

  if (resizeTimeout) {
    clearTimeout(resizeTimeout);
    resizeTimeout = null;
  }

  if (masonryInstance) {
    try {
      masonryInstance.destroy();
    } catch (error) {
      console.warn("Error destroying masonry instance on unmount:", error);
    }
    masonryInstance = null;
  }
});

const loadMasonryScript = () => {
  return new Promise((resolve, reject) => {
    // Check if Masonry is already loaded
    if (window.Masonry) {
      resolve();
      return;
    }

    const script = document.createElement("script");
    script.src =
      "https://unpkg.com/masonry-layout@4.2.2/dist/masonry.pkgd.min.js";

    // Add timeout for script loading
    const timeout = setTimeout(() => {
      console.warn("Masonry script loading timed out");
      reject(new Error("Script loading timeout"));
    }, 10000); // 10 second timeout

    script.onload = () => {
      clearTimeout(timeout);
      resolve();
    };

    script.onerror = (error) => {
      clearTimeout(timeout);
      console.error("Error loading Masonry script:", error);
      reject(error);
    };

    document.head.appendChild(script);
  });
};

const initMasonry = () => {
  const container = document.querySelector(".select-work");
  if (!container || !window.Masonry) {
    console.warn("Masonry container or library not found, retrying...");
    // Retry after a short delay
    setTimeout(() => {
      initMasonry();
    }, 100);
    return;
  }

  // Destroy existing instance if it exists
  if (masonryInstance) {
    try {
      masonryInstance.destroy();
    } catch (error) {
      console.warn("Error destroying masonry instance:", error);
    }
    masonryInstance = null;
  }

  // Reset any inline styles that might interfere
  const columns = container.querySelectorAll(".column");
  columns.forEach((column) => {
    column.style.position = "";
    column.style.left = "";
    column.style.top = "";
  });

  try {
    // Initialize Masonry
    masonryInstance = new window.Masonry(container, {
      itemSelector: ".column",
      columnWidth: ".column",
      gutter: 0,
      percentPosition: true,
      transitionDuration: "0.3s",
      resize: true,
      initLayout: true,
    });

    // Force initial layout
    setTimeout(() => {
      if (masonryInstance && typeof masonryInstance.layout === "function") {
        try {
          masonryInstance.layout();
        } catch (error) {
          console.warn("Error calling masonry layout:", error);
        }
      }
    }, 50);

    // Re-layout after images are fully loaded
    const images = container.querySelectorAll("img, video");
    let loadedCount = 0;
    const totalImages = images.length;

    const finalizeMasonrySetup = () => {
      // Final masonry layout
      if (masonryInstance && typeof masonryInstance.layout === "function") {
        setTimeout(() => {
          try {
            masonryInstance.layout();
            // Initialize cursor indicator only after masonry is fully settled
            if (customCursorRef.value) {
              customCursorRef.value.initializeCursor();
            }
          } catch (error) {
            console.warn("Error calling masonry layout:", error);
          }
        }, 50);
      }
    };

    if (totalImages === 0) {
      // No images to load, finalize setup immediately
      setTimeout(() => {
        finalizeMasonrySetup();
      }, 100);
      return;
    }

    const checkAllLoaded = () => {
      loadedCount++;
      if (loadedCount === totalImages) {
        finalizeMasonrySetup();
      }
    };

    images.forEach((img) => {
      if (img.tagName === "VIDEO") {
        if (img.readyState >= 2) {
          checkAllLoaded();
        } else {
          img.addEventListener("loadeddata", checkAllLoaded, { once: true });
        }
      } else {
        if (img.complete && img.naturalHeight !== 0) {
          checkAllLoaded();
        } else {
          img.addEventListener("load", checkAllLoaded, { once: true });
          img.addEventListener("error", checkAllLoaded, { once: true });
        }
      }
    });
  } catch (error) {
    console.error("Error initializing Masonry:", error);
    masonryInstance = null;
  }
};

const animateProjects = () => {
  const projects = document.querySelectorAll(".proj");
  projects.forEach((project, index) => {
    setTimeout(() => {
      project.classList.add("animate");
    }, index * 100);
  });
};
</script>

<style scoped lang="scss">
// No project-specific styles needed here anymore - they're in ProjectCard.vue
</style>
